/**
 * Permission Service
 *
 * Fetches and caches module-level permissions for the current user.
 */

import apiClient from './apiClient';

export type PermissionLevel = 'RW' | 'R' | 'NONE';
export type ModuleName = 'STOCK' | 'VENTES' | 'LOCATIONS' | 'CLIENTS' | 'REPORTING' | 'ADMIN';
export type PermissionMap = Record<ModuleName, PermissionLevel>;

let cachedPermissions: PermissionMap | null = null;

const permissionService = {
  /**
   * Fetch current user's permissions from the API.
   */
  async fetchPermissions(): Promise<PermissionMap> {
    const response = await apiClient.get('/auth/permissions');
    // response.data is the APIResponse envelope: { success, data: {...} }
    const envelope = response.data as { success: boolean; data: Record<string, string> };
    cachedPermissions = (envelope.data || {}) as PermissionMap;
    return cachedPermissions;
  },

  /**
   * Get cached permissions (returns null if not yet loaded).
   */
  getCached(): PermissionMap | null {
    return cachedPermissions;
  },

  /**
   * Clear the cache (call on logout or after admin saves).
   */
  clearCache(): void {
    cachedPermissions = null;
  },

  /**
   * Check if permissions map is valid (non-null and has at least one key).
   */
  isLoaded(permissions: PermissionMap | null): boolean {
    return !!permissions && Object.keys(permissions).length > 0;
  },

  /**
   * Check if the user has at least read access to a module.
   * Returns false if permissions aren't loaded yet (fail-closed for reads).
   */
  hasRead(permissions: PermissionMap | null, module: ModuleName): boolean {
    if (!this.isLoaded(permissions)) return false;
    const level = permissions![module];
    return level === 'R' || level === 'RW';
  },

  /**
   * Check if the user has write access to a module.
   * Returns false if permissions aren't loaded yet (fail-closed for writes).
   */
  hasWrite(permissions: PermissionMap | null, module: ModuleName): boolean {
    if (!this.isLoaded(permissions)) return false;
    return permissions![module] === 'RW';
  },
};

export default permissionService;
